import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_CONSTANTS } from "../../../API_CONSTANTS";
import { Spinner, Button, Card, Row, Col, Modal } from "react-bootstrap";
import Toast from "../../../Components/Toast/Toast";
import { faCheckCircle, faTimesCircle } from "@fortawesome/fontawesome-free-solid";
import surgeryBtnStyles from "../../../Style/surgeryButtons.module.css";

export default function BookedSurgeryOverview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [showToast, setShowToast] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [toastIcon, setToastIcon] = useState(faCheckCircle);
  const [iconColor, setIconColor] = useState("");
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);

  const handleToast = useCallback((message, icon, color, className) => {
    setToastMessage(message);
    setToastIcon(icon);
    setIconColor(color);
    setShowToast(`show ${className}`);
    setTimeout(() => setShowToast(""), 3000);
  }, []);

  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        setLoading(true);
        let response;
        
        // Fetching booking details for ID: id
        
        // Try fetching all bookings with pagination until we find the matching one
        const allBookings = [];
        let lastKey = null;
        let pageCount = 0;
        let found = null;
        
        const fetchPageUntilFound = async (cursor = null) => {
          try {
            const payload = cursor ? { lastKey: cursor } : {};
            
            let pageResponse;
            try {
              // Try POST first
              pageResponse = await axios.post(
                API_CONSTANTS.GET_BOOKED_PROCEDURE_AND_SURGERY(), 
                payload,
                {
                  headers: API_CONSTANTS.headers(),
                }
              );
            } catch (postErr) {
              // If POST fails, try GET
              pageResponse = await axios.get(
                `${API_CONSTANTS.GET_BOOKED_PROCEDURE_AND_SURGERY()}${cursor ? `?lastKey=${encodeURIComponent(cursor)}` : ''}`,
                {
                  headers: API_CONSTANTS.headers(),
                }
              );
            }
            
            const bookings = pageResponse.data?.response?.procedures || [];
            const nextLastKey = pageResponse.data?.response?.lastKey || null;
            
            pageCount++;
            
            // Search for matching booking in this page
            found = bookings.find(b => b && b.appointmentId === id);
            
            if (found) {
              setBooking(found);
              return true; // Found it
            }
            
            allBookings.push(...bookings);
            
            // If more pages exist and not found yet, fetch next page
            if (nextLastKey && !found) {
              return await fetchPageUntilFound(nextLastKey);
            }
            
            return false; // No more pages and not found
          } catch (err) {
            throw err;
          }
        };
        
        const foundBooking = await fetchPageUntilFound();
        
        if (!foundBooking) {
          handleToast("Booking not found", faTimesCircle, "#dc3545", "show-error");
        }
      } catch (error) {
        handleToast("Error fetching details", faTimesCircle, "#dc3545", "show-error");
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [id, handleToast]);

  const handleVerify = () => {
    // Open modal to confirm verification
    setShowVerifyModal(true);
  };

  const handleComplete = () => {
    
    try {
      // Step 1: Try to save to database
      const completePayload = {
        appointmentId: booking?.appointmentId,
        appoint_status: 'complete'
      };
      
      axios.post(
        API_CONSTANTS.VERIFY_PROCEDURE_AND_SURGERY(),
        completePayload,
        { headers: API_CONSTANTS.headers() }
      ).then(dbResponse => {
        // database saved
      });

      // Step 2: Update booking status locally
      setBooking(prev => ({
        ...prev,
        booking_status: 'completed'
      }));
      handleToast("Surgery Completed Successfully", faCheckCircle, "#28a745", "show-success");
    } catch (error) {
      handleToast("Error completing surgery", faTimesCircle, "#dc3545", "show-error");
    }
  };

  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    // ...existing cancel logic (API call, state update, toast)...
    try {
      // Step 1: Try to save to database
      const cancelPayload = {
        appointmentId: booking?.appointmentId,
        appoint_status: 'cancel'
      };
      
      axios.post(
        API_CONSTANTS.VERIFY_PROCEDURE_AND_SURGERY(),
        cancelPayload,
        { headers: API_CONSTANTS.headers() }
      ).then(dbResponse => {
        // database saved
      }).catch(dbErr => {
        // database save failed
      });

      // Step 2: Update booking status locally
      setBooking(prev => ({
        ...prev,
        booking_status: 'cancelled'
      }));

      handleToast("Booking Cancelled Successfully", faTimesCircle, "#dc3545", "show-danger");
    } catch (error) {
      handleToast("Error cancelling booking", faTimesCircle, "#dc3545", "show-danger");
    }
  };

  const handleOpenReschedule = () => {
    // Set initial date/time from current booking
    setRescheduleDate(booking?.appointment_time ? booking?.appointment_time.split(' ')[0] : booking?.procedureInfo?.date || "");
    setRescheduleTime("");
    setShowRescheduleModal(true);
  };

  const handleConfirmReschedule = async () => {
    setVerifying(true);
    try {
      if (!rescheduleDate) {
        handleToast("Please select a date", faTimesCircle, "#dc3545", "show-error");
        setVerifying(false);
        return;
      }

      // Combine date and time for appointment_time
      const appointmentTime = rescheduleTime ? `${rescheduleDate} ${rescheduleTime}` : rescheduleDate;

      // Step 1: Try to save to database
      // Attempting to save new date/time to database
      const reschedulePayload = {
        appointmentId: booking?.appointmentId,
        appointment_time: appointmentTime,
        appoint_status: 'reschedule'
      };
      // Payload being sent (logs removed)

      let dbSaved = false;
      try {
        const dbResponse = await axios.post(
          API_CONSTANTS.VERIFY_PROCEDURE_AND_SURGERY(),
          reschedulePayload,
          { headers: API_CONSTANTS.headers() }
        );

        dbSaved = true;
      } catch (dbErr) {
        // database save failed (reschedule)

        
        // Show warning toast
        handleToast(" Reschedule saved locally (database sync pending)", faTimesCircle, "#FFA500", "show-error");
      }

      // Step 2: Update booking in local state
      setBooking(prev => ({
        ...prev,
        booking_status: 'rescheduled',
        appointment_time: appointmentTime,
        procedureInfo: {
          ...prev.procedureInfo,
          date: rescheduleDate,
          time: rescheduleTime
        }
      }));
      // Local state updated - Status changed to 'rescheduled'

      // Step 3: Close modal
      setShowRescheduleModal(false);


      // Step 4: Show success message
      handleToast(
        dbSaved 
          ? "Appointment Rescheduled Successfully ✅" 
          : "Appointment Rescheduled (Database sync may be delayed) ⚠️", 
        faCheckCircle, 
        dbSaved ? "#009b94" : "#FFA500", 
        "show-success"
      );

    } catch (error) {
      handleToast("Error rescheduling appointment", faTimesCircle, "#dc3545", "show-error");
    } finally {
      setVerifying(false);
    }
  };

  const handleConfirmVerify = async () => {
    setVerifying(true);
    try {
      // Step 1: Try to save to database
      const verifiedTimestamp = new Date().toLocaleString();
      const verifyPayload = {
        appointmentId: booking?.appointmentId,
        appoint_status: 'verify',
        appointment_time: booking?.procedureInfo?.date || new Date().toISOString().split('T')[0],
        verified_date: verifiedTimestamp
      };
      
      try {
        const dbResponse = await axios.post(
          API_CONSTANTS.VERIFY_PROCEDURE_AND_SURGERY(),
          verifyPayload,
          { headers: API_CONSTANTS.headers() }
        );
        // database saved successfully
      } catch (dbErr) {
        // database save failed (verify)
      }

      // Step 2: Update booking status in local state (UI)
      setBooking(prev => ({
        ...prev,
        booking_status: 'verified',
        verified_date: verifiedTimestamp
      }));

      // Step 3: Close modal
      setShowVerifyModal(false);

      // Step 4: Show success message
      handleToast("Surgery Verified Successfully", faCheckCircle, "#009b94", "show-success");
      
    } catch (error) {
      handleToast("Error during verification", faTimesCircle, "#dc3545", "show-error");
    } finally {
      setVerifying(false);
    }
  };



  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="p-4 text-center">
        <h4>Booking not found</h4>
        <Button onClick={() => navigate("/surgery/booked")}>Back to List</Button>
        <Toast cName={showToast} text={toastMessage} icon={toastIcon} color={iconColor} />
      </div>
    );
  }

  const { procedureInfo } = booking;
  const primaryDoctor = procedureInfo?.doctors?.[0]; // Get first doctor from array
  const clinicDetails = primaryDoctor?.clinic;

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Booking Details</h3>
        <div className="d-flex gap-2">
          {/* Back to List - Always Show */}
          <Button 
            className={surgeryBtnStyles.surgeryActionBtn + " " + surgeryBtnStyles.btnList}
            onClick={() => navigate("/surgery/booked")}
          >
            Back to List
          </Button>

          {/* Verify Button - Only when pending */}
          {booking?.booking_status === 'pending' && (
            <Button
              className={surgeryBtnStyles.surgeryActionBtn + " " + surgeryBtnStyles.btnComplete}
              onClick={handleVerify}
            >Verify Surgery</Button>
          )}

          {/* Complete, Cancel, Reschedule - Only when verified or rescheduled */}
          {(booking?.booking_status === 'verified' || booking?.booking_status === 'rescheduled') && (
            <>
              <Button
                className={surgeryBtnStyles.surgeryActionBtn + " " + surgeryBtnStyles.btnComplete}
                onClick={handleComplete}
              >Complete</Button>
              <Button 
                className={surgeryBtnStyles.surgeryActionBtn + " " + surgeryBtnStyles.btnCancel}
                onClick={handleCancel}
              >Cancel</Button>
              <Button 
                className={surgeryBtnStyles.surgeryActionBtn + " " + surgeryBtnStyles.btnReschedule}
                onClick={handleOpenReschedule}
              >Reschedule</Button>
            </>
          )}
        </div>
      </div>

      <Card className="shadow-sm border-0">
        <Card.Body>
          <Row className="mb-4">
            <Col md={6}>
              <h5 className="text-muted mb-3">General Information</h5>
              {/* <div className="mb-2"><strong>Appointment ID:</strong> {booking?.appointmentId}</div> */}
              <div className="mb-2"><strong>Patient Name:</strong> {booking?.full_name}</div>
              <div className="mb-2"><strong>Procedure Name:</strong> {procedureInfo?.procedure_name}</div>
              <div className="mb-2"><strong>Procedure Type:</strong> {procedureInfo?.procedure_type}</div>
              <div className="mb-2">
                <strong>Status:</strong> 
                <span style={{ 
                  color: booking?.booking_status === 'verified' ? '#009B93' : 
                         booking?.booking_status === 'rescheduled' ? '#FFA500' :
                         booking?.booking_status === 'completed' ? '#28a745' : 
                         booking?.booking_status === 'cancelled' ? '#dc3545' : 'green',
                  fontWeight: "bold",
                  marginLeft: "8px",
                  textTransform: "capitalize"
                }}>
                  {booking?.booking_status}
                </span>
              </div>
              {booking?.verified_date && (
                <div className="mb-2"><strong>Verified At:</strong> <span style={{ color: "#009B93", fontWeight: "bold" }}>{booking?.verified_date}</span></div>
              )}
              <div className="mb-2"><strong>Booked At:</strong> {booking?.created_at ? new Date(booking?.created_at).toLocaleString() : "N/A"}</div>
            </Col>
            <Col md={6}>
              <h5 className="text-muted mb-3">Schedule & Pricing</h5>
              <div className="mb-2">
                <strong>Scheduled Date:</strong> {booking?.appointment_time ? booking?.appointment_time.split(' ')[0] : procedureInfo?.date}
              </div>
              
              {/* Show time if available from appointment_time or procedureInfo */}
              {booking?.appointment_time && booking?.appointment_time.split(' ')[1] && (
                <div className="mb-2"><strong>Scheduled Time:</strong> {booking?.appointment_time.split(' ')[1]}</div>
              )}
              {!booking?.appointment_time && procedureInfo?.time && (
                <div className="mb-2"><strong>Scheduled Time:</strong> {procedureInfo?.time}</div>
              )}
              
              <div className="mb-2"><strong>Price:</strong> PKR {booking?.charges}</div>
              <div className="mb-2"><strong>Payment Method:</strong> {booking?.paymentMethod}</div>
              <div className="mb-2"><strong>City:</strong> {booking?.city}</div>
              <div className="mb-2"><strong>State/Province:</strong> {booking?.state}</div>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={6}>
              <h5 className="text-muted mb-3">Doctor Details</h5>
              <div className="mb-2"><strong>Name:</strong> {primaryDoctor?.name || "N/A"}</div>
              <div className="mb-2"><strong>Specialization:</strong> {primaryDoctor?.specialization || "N/A"}</div>
              <div className="mb-2"><strong>Experience:</strong> {primaryDoctor?.experience ? primaryDoctor.experience + " years" : "N/A"}</div>
              <div className="mb-2"><strong>PMDC Verified:</strong> {primaryDoctor?.pmdc_verified ? "Yes" : "No"}</div>
            </Col>
            <Col md={6}>
              <h5 className="text-muted mb-3">Clinic Details</h5>
              <div className="mb-2"><strong>Name:</strong> {clinicDetails?.name || "N/A"}</div>
              <div className="mb-2"><strong>City:</strong> {clinicDetails?.city || "N/A"}</div>
              <div className="mb-2"><strong>Address:</strong> {clinicDetails?.address || "N/A"}</div>
            </Col>
          </Row>

          {procedureInfo?.doctors && procedureInfo.doctors.length > 1 && (
            <Row className="mb-4">
              <Col>
                <h5 className="text-muted mb-3">Additional Doctors</h5>
                {procedureInfo.doctors.slice(1).map((doc, idx) => (
                  <div key={idx} className="mb-3 p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                    <div className="mb-2"><strong>Name:</strong> {doc?.name}</div>
                    <div className="mb-2"><strong>Specialization:</strong> {doc?.specialization}</div>
                    <div className="mb-2"><strong>Experience:</strong> {doc?.experience ? doc.experience + " years" : "N/A"}</div>
                  </div>
                ))}
              </Col>
            </Row>
          )}

          <Row>
            <Col>
              <h5 className="text-muted mb-3">Description</h5>
              <p>{procedureInfo?.description || "No description available"}</p>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      <Toast cName={showToast} text={toastMessage} icon={toastIcon} color={iconColor} />

      {/* Verify Surgery Confirmation Modal */}
      <Modal show={showVerifyModal} onHide={() => setShowVerifyModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Verify Surgery</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h6 className="mb-3 text-muted">Surgery Details</h6>
          <Row className="mb-4">
            <Col md={6}>
              <div className="mb-3 p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                <div className="mb-2"><strong>Appointment ID:</strong> <br />{booking?.appointmentId}</div>
                <div className="mb-2"><strong>Patient Name:</strong> <br />{booking?.full_name}</div>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3 p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                <div className="mb-2"><strong>Procedure:</strong> <br />{procedureInfo?.procedure_name}</div>
                <div className="mb-2"><strong>Scheduled Date:</strong> <br />{booking?.appointment_time ? booking?.appointment_time.split(' ')[0] : procedureInfo?.date}</div>
              </div>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={6}>
              <div className="p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                <div className="mb-2"><strong>Doctor:</strong> <br />{procedureInfo?.doctors?.[0]?.name}</div>
                <div className="mb-2"><strong>Specialization:</strong> <br />{procedureInfo?.doctors?.[0]?.specialization}</div>
              </div>
            </Col>
            <Col md={6}>
              <div className="p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                <div className="mb-2"><strong>Clinic:</strong> <br />{procedureInfo?.doctors?.[0]?.clinic?.name}</div>
                <div className="mb-2"><strong>Price:</strong> <br />PKR {booking?.charges}</div>
              </div>
            </Col>
          </Row>


        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => setShowVerifyModal(false)}
            disabled={verifying}
            style={{ background: "#6c757d", color: "#fff", border: "none", boxShadow: "none" }}
          >
            Close
          </Button>
          <Button
            onClick={handleConfirmVerify}
            disabled={verifying}
            style={{ backgroundColor: "#009B93", borderColor: "#009B93", color: "#fff", border: "none", boxShadow: "none" }}
          >
            {verifying ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Verifying...
              </>
            ) : (
              "✓ Confirm Verify"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reschedule Appointment Modal */}
      <Modal show={showRescheduleModal} onHide={() => setShowRescheduleModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Reschedule Surgery</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h6 className="mb-3 text-muted">Change Appointment Date & Time</h6>
          
          <div className="mb-4">
            <label className="form-label"><strong>Current Date</strong></label>
            <div className="alert alert-info">
              {booking?.appointment_time ? booking?.appointment_time.split(' ')[0] : booking?.procedureInfo?.date}
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label"><strong>New Date *</strong></label>
            <input
              type="date"
              className="form-control"
              value={rescheduleDate}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => setRescheduleDate(e.target.value)}
            />
          </div>

          <div className="mb-4">
            <label className="form-label"><strong>New Time (Optional)</strong></label>
            <input
              type="time"
              className="form-control"
              value={rescheduleTime}
              onChange={(e) => setRescheduleTime(e.target.value)}
            />
          </div>

          <Row className="mb-4">
            <Col md={6}>
              <div className="p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                <div className="mb-2"><strong>Doctor:</strong> <br />{procedureInfo?.doctors?.[0]?.name}</div>
              </div>
            </Col>
            <Col md={6}>
              <div className="p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                <div className="mb-2"><strong>Procedure:</strong> <br />{procedureInfo?.procedure_name}</div>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => setShowRescheduleModal(false)}
            disabled={verifying}
            style={{ background: "#6c757d", color: "#fff", border: "none", boxShadow: "none" }}
          >
            Close
          </Button>
          <Button
            onClick={handleConfirmReschedule}
            disabled={verifying || !rescheduleDate}
            style={{ background: "#FFA500", color: "#fff", border: "none", boxShadow: "none" }}
          >
            {verifying ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Rescheduling...
              </>
            ) : (
              "✓ Confirm Reschedule"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Booking</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ textAlign: "center", padding: "20px" }}>
            <h5 style={{ color: "#dc3545", fontWeight: "bold" }}>Are you sure you want to cancel this booking?</h5>
            <p>This action cannot be undone.</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            onClick={() => setShowCancelModal(false)}
            style={{ background: "#6c757d", color: "#fff", border: "none", boxShadow: "none" }}
          >
            No, Keep Booking
          </Button>
          <Button 
            onClick={handleConfirmCancel}
            style={{ background: "#dc3545", color: "#fff", border: "none", boxShadow: "none" }}
          >
            Yes, Cancel Booking
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx="true">
        {`
          /* No inline styles needed - all styling in surgeryButtons.module.css */
        `}
      </style>
    </div>
  );
}
